import { useEffect, useState, useCallback } from "react";
import { Box, Text, Button, Stack, HStack } from "@chakra-ui/react";
import { WorkerPicker } from "@/components/WorkerPicker";
import { CalendarPlus } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { apiPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeAll } from "@/lib/offline";
import { Modal } from "@/components/Modal";
import { showToast } from "@/components/AppToaster";
import { colors } from "@/theme/colors";

const Sel = ({ value, onChange, children, testId }) => (
  <Box as="select" value={value} onChange={onChange} data-testid={testId} w="full" px="3" py="2.5"
    borderRadius="8px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm">{children}</Box>
);
const Lbl = ({ children }) => <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>{children}</Text>;
const inp = { width: "100%", padding: "10px 12px", borderRadius: "8px", border: `1px solid ${colors.border}`, fontSize: "14px", background: "#fff" };

// Manager tool: assign a form to specific workers. Document acknowledgements
// now live in the Resources tab (RequestAckModal).
export function ScheduleFormButton({ variant = "solid", size = "sm" }) {
  const { role, activeCompanyId } = useAuth();
  const [open, setOpen] = useState(false);
  if (!["org_admin", "supervisor"].includes(role)) return null;
  return (
    <>
      <Button size={size} variant={variant} bg={variant === "solid" ? colors.teal : undefined}
        color={variant === "solid" ? "white" : colors.textPrimary}
        _hover={{ bg: variant === "solid" ? colors.tealHover : colors.cream }}
        onClick={() => setOpen(true)} data-testid="schedule-form-btn">
        <CalendarPlus size={16} /> Schedule Form
      </Button>
      {open && <ScheduleFormModal companyId={activeCompanyId} onClose={() => setOpen(false)} />}
    </>
  );
}

function ScheduleFormModal({ companyId, onClose }) {
  const { user } = useAuth();
  // Scope pickers to the company this form is being scheduled FOR.
  const activeCompanyId = companyId;
  const today = new Date().toISOString().slice(0, 10);
  const [templates, setTemplates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [f, setF] = useState({
    template_id: "", location_id: "",
    worker_ids: [], signer_ids: [], instructions: "", due_date: today,
    recurrence: "once", recurrence_end: "", occurrences: "",
  });
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    const [tRes, lRes, wRes] = await safeAll([
      supabase.from("templates").select("id,title").eq("status", "published")
        .or(`company_id.is.null,company_id.eq.${activeCompanyId}`).order("title"),
      supabase.from("locations").select("id,name").eq("active", true)
        .eq("company_id", activeCompanyId).order("name"),
      supabase.from("workers").select("id,full_name,job_role").eq("status", "active")
        .eq("company_id", activeCompanyId).not("profile_id", "is", null).order("full_name"),
    ]);
    if (tRes.error || lRes.error || wRes.error) {
      // Offline: pickers fall back to the same cached options as the screens.
      const snap = cacheGet(user.id, "pickers") || {};
      setTemplates(snap.templates || []); setLocations(snap.locations || []); setWorkers(snap.workers || []);
    } else {
      setTemplates(tRes.data || []); setLocations(lRes.data || []); setWorkers(wRes.data || []);
      cachePut(user.id, "pickers", {
        ...(cacheGet(user.id, "pickers") || {}),
        templates: tRes.data || [], locations: lRes.data || [], workers: wRes.data || [],
      });
    }
    setReady(true);
  }, [user.id, activeCompanyId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setErr(null);
    if (f.worker_ids.length === 0) { setErr("Assign at least one worker."); return; }
    if (!f.template_id) { setErr("Choose a form."); return; }
    setBusy(true);
    try {
      const signers = workers.filter((w) => f.signer_ids.includes(w.id)).map((w) => ({ worker_id: w.id, name: w.full_name }));
      const res = await apiPost("/assignments/create", {
        kind: "form", template_id: f.template_id, resource_id: null,
        location_id: f.location_id || null, worker_ids: f.worker_ids, instructions: f.instructions.trim() || null,
        additional_signers: signers, recurrence: f.recurrence, due_date: f.due_date,
        recurrence_end: f.recurrence === "once" ? null : (f.recurrence_end || null),
        occurrences: f.recurrence === "once" ? null : (f.occurrences ? Number(f.occurrences) : null),
        redirect_to: window.location.origin,
      });
      showToast(`Scheduled — ${res.created} task${res.created === 1 ? "" : "s"} assigned${res.emailed ? `, ${res.emailed} notified` : ""}`);
      onClose();
    } catch (e) {
      setErr(navigator.onLine ? e.message : "You're offline — this action needs a connection. Your selections stay on screen; try again once you reconnect.");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Schedule a form" testId="schedule-form-modal">
      <Stack gap="3.5">
        {err && <Box px="3" py="2" borderRadius="8px" bg={colors.criticalBg} color={colors.criticalText} fontSize="sm" data-testid="schedule-error">{err}</Box>}

        <Box>
          <Lbl>Set Due Date</Lbl>
          <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} style={inp} data-testid="schedule-due-date" />
        </Box>

        <Box>
          <Lbl>Select Form</Lbl>
          <Sel value={f.template_id} onChange={(e) => setF({ ...f, template_id: e.target.value })} testId="schedule-template">
            <option value="">{ready ? "Choose a published form…" : "Loading…"}</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
          </Sel>
        </Box>

        <Box>
          <Lbl>Add Instructions (optional)</Lbl>
          <textarea value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} rows={2} style={{ ...inp, resize: "vertical" }} placeholder="Note to the assignee…" data-testid="schedule-instructions" />
        </Box>

        <Box>
          <Lbl>Select Location</Lbl>
          <Sel value={f.location_id} onChange={(e) => setF({ ...f, location_id: e.target.value })} testId="schedule-location">
            <option value="">No specific site</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Sel>
        </Box>

        <Box>
          <Lbl>Assign Worker(s)</Lbl>
          <WorkerPicker workers={workers} multi value={f.worker_ids}
            onChange={(ids) => setF((s) => ({ ...s, worker_ids: ids }))}
            placeholder={ready ? "Select workers" : "Loading…"} testId="schedule-workers" />
        </Box>

        <Box>
          <Lbl>Schedule Additional Signatures (optional)</Lbl>
          <WorkerPicker workers={workers} multi value={f.signer_ids}
            onChange={(ids) => setF((s) => ({ ...s, signer_ids: ids }))}
            placeholder="Add required signers" testId="schedule-signers" />
        </Box>

        <Box>
          <Lbl>Recurrence</Lbl>
          <Sel value={f.recurrence} onChange={(e) => setF({ ...f, recurrence: e.target.value })} testId="schedule-recurrence">
            <option value="once">One-off (by due date)</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </Sel>
          {f.recurrence !== "once" && (
            <HStack gap="2" mt="2">
              <Box flex="1">
                <Text fontSize="xs" color={colors.textSecondary} mb="1">End date</Text>
                <input type="date" value={f.recurrence_end} onChange={(e) => setF({ ...f, recurrence_end: e.target.value, occurrences: "" })} style={inp} data-testid="schedule-end-date" />
              </Box>
              <Text fontSize="xs" color={colors.textSecondary} pt="4">or</Text>
              <Box flex="1">
                <Text fontSize="xs" color={colors.textSecondary} mb="1"># occurrences</Text>
                <input type="number" min="1" max="52" value={f.occurrences} onChange={(e) => setF({ ...f, occurrences: e.target.value, recurrence_end: "" })} style={inp} placeholder="e.g. 8" data-testid="schedule-occurrences" />
              </Box>
            </HStack>
          )}
        </Box>

        <Button onClick={submit} loading={busy} data-testid="schedule-submit">Schedule</Button>
      </Stack>
    </Modal>
  );
}
